/**
 * Peças do quadro de verificação (E74.F1): pílula de situação, tabela de itens
 * (integridade / identidade) e linha do tempo. Tudo rotulado por chave estável
 * do contrato — nunca pelo código cru.
 */
import { AlertTriangle, CheckCircle2, CircleDashed, CircleHelp, XCircle } from 'lucide-react';

import {
  Badge,
  MonoData,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  type BadgeProps,
} from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type {
  ClassificacaoIpData,
  EventoLinhaDoTempoData,
  GrauConvergencia,
  ItemVerificacaoData,
  ResultadoExameEletronicoData,
  SituacaoItem,
} from '@/lib/exame_eletronico';
import type { Tendencia } from '@/lib/pre_analise';
import { rotuloDaTendencia, vereditoDaTendencia, VEREDITO_TOM } from '@/lib/veredito';

const t = ptBR.exameEletronico.quadro;

/** Título de seção do quadro — o mesmo nos dois desenhos (item a item e por eixo). */
export function Titulo({ children }: { children: string }): JSX.Element {
  return (
    <h4 className="mb-sc-2 font-titulo text-[13px] uppercase tracking-[0.06em] text-fg-muted">
      {children}
    </h4>
  );
}

const SITUACAO_META: Record<
  SituacaoItem,
  { variant: NonNullable<BadgeProps['variant']>; icon: typeof CheckCircle2 }
> = {
  confere: { variant: 'success', icon: CheckCircle2 },
  nao_confere: { variant: 'danger', icon: XCircle },
  ausente: { variant: 'warning', icon: CircleDashed },
  // E162.F1.T07 — inconclusivo: o dado veio e o material não o confirma nem contradiz.
  inconclusivo: { variant: 'warning', icon: CircleHelp },
  nao_executado: { variant: 'neutral', icon: CircleHelp },
};

export function SituacaoBadge({ situacao }: { situacao: SituacaoItem }): JSX.Element {
  const meta = SITUACAO_META[situacao];
  const Icon = meta.icon;
  return (
    <Badge
      variant={meta.variant}
      icon={<Icon className="h-3 w-3" aria-hidden />}
      data-situacao={situacao}
    >
      {t.situacao[situacao]}
    </Badge>
  );
}

/** Rótulo do item por chave; chave desconhecida cai no texto da própria chave. */
function rotuloDoItem(chave: string): string {
  const itens: Record<string, string> = t.itens;
  return itens[chave] ?? chave;
}

export function TabelaItens({
  titulo,
  itens,
  testId,
}: {
  titulo: string;
  itens: readonly ItemVerificacaoData[];
  testId: string;
}): JSX.Element {
  return (
    <section data-testid={testId}>
      <h4 className="mb-sc-2 font-titulo text-[13px] uppercase tracking-[0.06em] text-fg-muted">
        {titulo}
      </h4>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t.colunaItem}</TableHead>
              <TableHead>{t.colunaSituacao}</TableHead>
              <TableHead>{t.colunaEsperado}</TableHead>
              <TableHead>{t.colunaObtido}</TableHead>
              <TableHead>{t.colunaDetalhe}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {itens.map((item) => (
              <TableRow key={item.chave} data-testid={`${testId}-${item.chave}`}>
                <TableCell className="whitespace-nowrap">{rotuloDoItem(item.chave)}</TableCell>
                <TableCell>
                  <SituacaoBadge situacao={item.situacao} />
                </TableCell>
                <TableCell className="max-w-[16rem] break-all">
                  {item.esperado ? <MonoData>{item.esperado}</MonoData> : '—'}
                </TableCell>
                <TableCell className="max-w-[16rem] break-all">
                  {item.obtido ? <MonoData>{item.obtido}</MonoData> : '—'}
                </TableCell>
                <TableCell className="text-body-sm text-fg-muted">{item.detalhe ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

const FORMATO_UTC = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'medium',
  timeZone: 'UTC',
});
const FORMATO_LOCAL = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'medium' });

export function formatarInstante(iso: string | null | undefined, emUtc: boolean): string {
  if (!iso) return t.semHorario;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return (emUtc ? FORMATO_UTC : FORMATO_LOCAL).format(d);
}

export function LinhaDoTempo({
  eventos,
  sequenciaIlogica,
}: {
  eventos: readonly EventoLinhaDoTempoData[];
  sequenciaIlogica: boolean;
}): JSX.Element {
  const tipos = ptBR.exameEletronico.form.eventoTipoOpcoes;
  return (
    <section data-testid="quadro-linha-do-tempo">
      <h4 className="mb-sc-2 font-titulo text-[13px] uppercase tracking-[0.06em] text-fg-muted">
        {t.linhaDoTempo}
      </h4>
      {sequenciaIlogica ? (
        <p
          className="mb-sc-2 border border-danger bg-danger-50 px-sc-3 py-sc-2 text-body-sm text-danger-700"
          role="alert"
          data-testid="quadro-sequencia-ilogica"
        >
          {t.sequenciaIlogica}
        </p>
      ) : null}
      {eventos.length === 0 ? (
        <p className="text-body-sm text-fg-dim">{t.linhaDoTempoVazia}</p>
      ) : (
        <ol className="space-y-sc-1">
          {eventos.map((evento, indice) => (
            <li
              key={`${evento.tipo}-${indice}`}
              className="flex flex-wrap items-center gap-sc-2 border-l-2 border-border pl-sc-3 text-body-sm"
              data-testid={`quadro-evento-${indice}`}
            >
              <span className="font-medium text-fg">{tipos[evento.tipo]}</span>
              {/* E162.F1.T07 (QA-05): o rótulo que a fonte imprimiu, ao lado do tipo. */}
              {evento.descricao ? (
                <span className="text-fg-muted" data-testid={`quadro-evento-${indice}-descricao`}>
                  {evento.descricao}
                </span>
              ) : null}
              <MonoData
                as="time"
                variant="muted"
                {...(evento.em_utc ? { dateTime: evento.em_utc } : {})}
              >
                {formatarInstante(evento.em_utc, true)} UTC
              </MonoData>
              <MonoData variant="muted">{formatarInstante(evento.em_local, false)}</MonoData>
              {evento.fora_de_ordem ? <Badge variant="danger">{t.foraDeOrdem}</Badge> : null}
              {evento.utc_local_incoerentes ? (
                <Badge variant="warning">{t.utcLocalIncoerentes}</Badge>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

/** E168.F3.C01 (QA de 24/10): o papel que o exame apura — a rede de entrega não é o signatário. */
export function papelDoIp(ip: ClassificacaoIpData): string {
  return ip.rede_de_entrega ? t.ipRedeDeEntrega : ptBR.exameEletronico.form.ipPapelOpcoes[ip.papel];
}

/** E168.F3.C01: a rede da instituição não é residencial nem empresarial do signatário. */
export function naturezaDoIp(ip: ClassificacaoIpData): string {
  return ip.rede_da_instituicao
    ? t.ipRedeDaInstituicao.replace('{instituicao}', ip.rede_da_instituicao)
    : t.ipTipo[ip.tipo];
}

export function ListaDeIps({
  ips,
}: {
  ips: ResultadoExameEletronicoData['ips'];
}): JSX.Element | null {
  if (ips.length === 0) return null;
  return (
    <section data-testid="quadro-ips">
      <Titulo>{t.ips}</Titulo>
      <ul className="space-y-sc-1 text-body-sm">
        {ips.map((ip) => (
          <li
            key={`${ip.papel}-${ip.endereco}`}
            className="flex flex-wrap items-center gap-sc-2"
            data-testid={`quadro-ip-${ip.endereco}`}
            data-tipo={ip.tipo}
          >
            <MonoData>{ip.endereco}</MonoData>
            <span className="text-fg-muted">{papelDoIp(ip)}</span>
            <span className="text-fg-muted">{naturezaDoIp(ip)}</span>
            {ip.provedor ? <span className="text-fg-muted">{ip.provedor}</span> : null}
            {ip.asn ? (
              <span className="text-fg-muted">
                {t.ipAsn} <MonoData variant="muted">{ip.asn}</MonoData>
              </span>
            ) : null}
            <SituacaoBadge situacao={ip.situacao} />
            {ip.detalhe ? <span className="text-fg-dim">{ip.detalhe}</span> : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function RedFlags({
  flags,
}: {
  flags: ResultadoExameEletronicoData['red_flags'];
}): JSX.Element {
  return (
    <section data-testid="quadro-red-flags">
      <Titulo>{t.redFlags}</Titulo>
      {flags.length === 0 ? (
        <p className="text-body-sm text-fg-dim">{t.redFlagsVazio}</p>
      ) : (
        <ul className="space-y-sc-1">
          {flags.map((flag) => (
            <li
              key={flag.codigo}
              className="flex flex-wrap items-center gap-sc-2 text-body-sm"
              data-testid={`quadro-red-flag-${flag.codigo}`}
            >
              <AlertTriangle
                className={`h-4 w-4 ${flag.severidade === 'alta' ? 'text-danger' : 'text-warning-700'}`}
                aria-hidden
              />
              <Badge variant={flag.severidade === 'alta' ? 'danger' : 'warning'}>
                {t.severidade[flag.severidade]}
              </Badge>
              <span className="text-fg">{flag.detalhe ?? flag.codigo}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function Ausencias({ ausencias }: { ausencias: readonly string[] }): JSX.Element {
  return (
    <section data-testid="quadro-ausencias">
      <Titulo>{t.ausencias}</Titulo>
      {ausencias.length === 0 ? (
        <p className="text-body-sm text-fg-dim">{t.ausenciasVazio}</p>
      ) : (
        <ul className="list-disc space-y-sc-1 pl-sc-4 text-body-sm text-fg-muted">
          {ausencias.map((ausencia) => (
            <li key={ausencia}>{ausencia}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Tom do grau apurado — o mesmo nos dois desenhos do quadro. */
export const GRAU_VARIANT: Record<GrauConvergencia, NonNullable<BadgeProps['variant']>> = {
  forte: 'success',
  medio: 'success',
  fraco: 'warning',
  inconclusivo: 'warning',
  inconsistente: 'danger',
};

/**
 * A régua da peça: grau apurado e tendência.
 *
 * A tendência sai da fonte única do veredito (`lib/veredito`), nunca de um
 * rótulo escrito aqui; `null` é o exame sem tendência gravada e não imprime
 * badge nenhum.
 */
export function GrauETendencia({
  grau,
  tendencia,
}: {
  grau: GrauConvergencia;
  tendencia: Tendencia | null;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-x-sc-6 gap-y-sc-2">
      <div className="flex items-center gap-sc-2">
        <span className="text-caption text-fg-dim">{t.grau}</span>
        <Badge variant={GRAU_VARIANT[grau]} data-testid="quadro-grau" data-grau={grau}>
          {t.grauOpcoes[grau]}
        </Badge>
      </div>
      {tendencia !== null ? (
        <div className="flex items-center gap-sc-2">
          <span className="text-caption text-fg-dim">{t.tendencia}</span>
          <Badge
            variant={VEREDITO_TOM[vereditoDaTendencia(tendencia)]}
            data-testid="quadro-tendencia"
            data-tendencia={tendencia}
          >
            {rotuloDaTendencia(tendencia)}
          </Badge>
        </div>
      ) : null}
    </div>
  );
}
